import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Loading from '../../Common/Loading';
import EL_ViewAvatarPurchases from './EL_ViewAvatarPurchases';
import EL_AvatarARD from './EL_AvatarARD';
import CustomAlert from '../../Common/CustomAlert';
import alert_A_bg from '../../Common/Images/ImagesForAlert/alert_A_bg.png';
import alert_bg from '../../Common/Images/ImagesForAlert/alert_Q_bg_small.png';
import ok_n from '../../Common/Images/ImagesForAlert/ok_n.png';
import ok_o from '../../Common/Images/ImagesForAlert/ok_o.png';
import yes_n from '../../Common/Images/ImagesForAlert/yes_n.png';
import yes_o from '../../Common/Images/ImagesForAlert/yes_o.png';
import no_n from '../../Common/Images/ImagesForAlert/no_n.png';
import no_o from '../../Common/Images/ImagesForAlert/no_o.png';

interface Avatar {
    Title: string;
    DateAdded: string;
    Type: string;
    Price: number;
    StartDate: string;
    AllocatedTo: string;
    Purchased: number;
    ActiveUsage: number;
}

const styles = {
    body: {
        margin: '0px',
        fontFamily: 'Trebuchet MS, Arial, Helvetica, sans-serif',
        fontSize: '10pt',
        backgroundColor: 'white',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column' as const,
    },
    container: {
        padding: '5px',
        flex: 1,
        display: 'flex',
        flexDirection: 'column' as const,
        overflow: 'hidden',
    },
    titleBar: {
        color: 'White',
        backgroundColor: 'Maroon',
        padding: '5px',
        fontSize: '12pt',
        fontFamily: 'Arial',
        width: '99.5%',
        textAlign: 'center' as const,
        fontWeight: 'bold',
        marginBottom: '5px',
    },
    headerRow: {
        fontFamily: 'Arial',
        fontSize: '10pt',
        backgroundColor: '#c7ddfd',
        color: '#2859ac',
        height: '32px',
        textAlign: 'left' as const,
    },
    headerCell: {
        padding: '8px',
        borderRight: '1px solid #80A0FF',
        borderBottom: '1px solid #a3c0e8',
        textAlign: 'left' as const,
        whiteSpace: 'nowrap' as const,
        overflow: 'hidden',
        fontWeight: 'normal' as const,
    },
    row: {
        fontFamily: 'Arial',
        fontSize: '11pt',
        backgroundColor: 'white',
        verticalAlign: 'text-top',
        cursor: 'pointer',
        height: '20px',
        color: 'black',
    },
    altRow: {
        fontFamily: 'Trebuchet MS',
        fontSize: '11pt',
        backgroundColor: '#F4FAFF',
        verticalAlign: 'text-top',
        cursor: 'pointer',
        height: '20px',
        color: 'black',
    },
    selectedRow: {
        fontFamily: 'Trebuchet MS',
        fontSize: '11pt',
        backgroundColor: 'orange',
        color: 'White',
        verticalAlign: 'text-top',
        cursor: 'pointer',
        height: '20px',
    },
    cell: {
        padding: '8px',
        fontSize: '10pt',
        borderRight: '1px solid #a3c0e8',
        whiteSpace: 'nowrap' as const,
        overflow: 'hidden',
        fontFamily: 'Arial',
        textOverflow: 'ellipsis',
    },
    paginationBar: {
        backgroundColor: '#c7ddfd',
        color: '#2859ac',
        fontFamily: 'Arial',
        fontSize: '10pt',
        fontWeight: 'bold' as const,
        textAlign: 'center' as const,
        padding: '8px',
        border: 'solid 1px #80A0FF',
        borderTop: 'solid 1px #80A0FF',
        width: '99%',
    },
    navLink: {
        textDecoration: 'none',
        color: '#003366',
        margin: '0 5px',
        cursor: 'pointer',
    },
    buttonContainer: {
        paddingTop: '10px',
        display: 'flex',
        justifyContent: 'center',
        gap: '15px',
    },
    button: {
        backgroundColor: '#E5E5E5',
        fontFamily: 'Arial',
        fontSize: '10pt',
        color: 'black',
        border: '1px solid #767676',
        height: '25px',
        width: '100px',
        cursor: 'pointer',
        whiteSpace: 'nowrap' as const,
        padding: '0px',
        borderRadius: '3px',
    },
    buttonDisabled: {
        backgroundColor: '#e0e0e0',
        fontFamily: 'Arial',
        fontSize: '9pt',
        color: '#a0a0a0',
        border: '1px solid #80A0FF',
        height: '25px',
        width: '100px',
        cursor: 'default',
        whiteSpace: 'nowrap' as const,
        backgroundImage: 'none',
    },
    scrollableDiv: {
        height: 'calc(100vh - 140px)',
        overflowY: 'auto' as const,
        overflowX: 'hidden' as const,
        border: '1px solid #80A0FF',
        width: '100%',
        backgroundColor: 'white',
    }
};

const API_BASE_URL = process.env.REACT_APP_DOCUMENT_LINK;

// Route of the Free Avatar Usage page (VB: EL_ViewFreeAvatarUsage.aspx).
// Must match the <Route path=...> you registered for FreeAvatarUsage.
const FREE_AVATAR_USAGE_PATH = '/WebAdmin/POINTS/EL_ViewFreeAvatarUsage';

export default function EL_ManageAvatars() {
    const navigate = useNavigate();
    const [avatars, setAvatars] = useState<Avatar[]>([]);
    const [selectedAvatar, setSelectedAvatar] = useState<Avatar | null>(null);
    const [loading, setLoading] = useState(true);
    const [showPurchases, setShowPurchases] = useState(false);
    const [showARD, setShowARD] = useState(false);
    const [ardMode, setArdMode] = useState<'A' | 'R'>('A');
    const [currentPage, setCurrentPage] = useState(1);
    const [alertMsg, setAlertMsg] = useState('');
    const [confirmConfig, setConfirmConfig] = useState<{ msg: string, onConfirm: () => void } | null>(null);
    const handleShowWarning = (msg: string) => setAlertMsg(msg);

    const handleShowConfirm = (msg: string, onConfirm: () => void) => {
        setConfirmConfig({ msg, onConfirm });
    };

    const handleConfirmYes = () => {
        if (confirmConfig) confirmConfig.onConfirm();
        setConfirmConfig(null);
    };

    const rowsPerPage = 50;

    const WarningDialog = () => (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 20000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div style={{ width: '400px', height: '200px', backgroundImage: `url(${alert_A_bg})`, backgroundRepeat: 'no-repeat', backgroundSize: '100% 100%', position: 'relative', fontFamily: 'Tahoma, Arial, sans-serif', fontSize: '12px', color: 'black' }}>
                <div style={{ position: 'absolute', top: '0', left: '0', width: '100%', height: '30px', display: 'flex', justifyContent: 'center', alignItems: 'center', color: 'white', fontWeight: 'bold' }}>Warning</div>
                <div style={{ position: 'absolute', top: '40px', left: '100px', right: '15px', bottom: '50px', display: 'flex', alignItems: 'center', justifyContent: 'flex-start' }}>
                    <span style={{ fontWeight: 'normal', marginTop: '-25px', fontSize: '11pt' }}>{alertMsg}</span>
                </div>
                <div style={{ position: 'absolute', bottom: '10px', width: '100%', textAlign: 'center' }}>
                    <img src={ok_n} alt="OK" onClick={() => setAlertMsg('')} style={{ cursor: 'pointer' }} onMouseOver={(e) => e.currentTarget.src = ok_o} onMouseOut={(e) => e.currentTarget.src = ok_n} />
                </div>
            </div>
        </div>
    );

    const ConfirmDialog = () => (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 20000, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <div style={{ width: '306px', height: '210px', backgroundImage: `url(${alert_bg})`, backgroundRepeat: 'no-repeat', position: 'relative', fontFamily: 'Tahoma', fontSize: '13px' }}>
                <div style={{ position: 'absolute', top: '5px', left: '0', width: '100%', textAlign: 'center', color: 'white', fontWeight: 'bold', fontSize: '13px' }}>Confirmation</div>
                <div style={{ position: 'absolute', top: '35px', left: '70px', right: '10px', height: '110px', display: 'flex', alignItems: 'center', color: '#525252', fontSize: '11pt' }}>
                    {confirmConfig?.msg}
                </div>
                <div style={{ position: 'absolute', bottom: '15px', width: '100%', textAlign: 'center' }}>
                    <img src={yes_n} alt="Yes" onClick={handleConfirmYes} style={{ cursor: 'pointer', margin: '-10px 15px' }} onMouseOver={(e) => e.currentTarget.src = yes_o} onMouseOut={(e) => e.currentTarget.src = yes_n} />
                    <img src={no_n} alt="No" onClick={() => setConfirmConfig(null)} style={{ cursor: 'pointer', margin: '-10px 15px' }} onMouseOver={(e) => e.currentTarget.src = no_o} onMouseOut={(e) => e.currentTarget.src = no_n} />
                </div>
            </div>
        </div>
    );

    useEffect(() => {
        fetchAvatars();
    }, []);

    const fetchAvatars = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_BASE_URL}api/manage_avatars/`);
            if (!response.ok) throw new Error('Network response was not ok');
            const data = await response.json();
            setAvatars(data);
        } catch (error) {
            console.error("Error fetching avatars:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleRowClick = (avatar: Avatar) => {
        if (!selectedAvatar || selectedAvatar.Title !== avatar.Title) {
            setSelectedAvatar(avatar);
        }
    };

    const formatDate = (dateString: string) => {
        if (!dateString) return "";
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    };

    const totalPages = Math.ceil(avatars.length / rowsPerPage);
    const startIndex = (currentPage - 1) * rowsPerPage;
    const currentData = avatars.slice(startIndex, startIndex + rowsPerPage);

    const handlePageChange = (direction: string) => {
        if (direction === 'first') setCurrentPage(1);
        if (direction === 'prev' && currentPage > 1) setCurrentPage(p => p - 1);
        if (direction === 'next' && currentPage < totalPages) setCurrentPage(p => p + 1);
        if (direction === 'last') setCurrentPage(totalPages);
    };

    const fn_ShowARDAvatar = (pARD: string) => {
        if (pARD === 'A') {
            setArdMode('A');
            setShowARD(true);
        } else if (pARD === 'R') {
            if (selectedAvatar) {
                setArdMode('R');
                setShowARD(true);
            }
        }
    };

    const fn_ViewPurchases = () => {
        if (selectedAvatar) {
            setShowPurchases(true);
        } else {
            setAlertMsg('Please Select A Row!');
            return;
        }
    };

    // VB: btnFreeAvatars_Click -> Response.Redirect("EL_ViewFreeAvatarUsage.aspx")
    const fn_FreeAvatars = () => {
        navigate(FREE_AVATAR_USAGE_PATH);
    };

    const fn_Back = () => {
        navigate('/SysAdmin');
    };

    return (
        <div style={styles.body}>
            <div style={styles.container}>
                <div style={styles.titleBar}>
                    My Incentives Manager Avatars
                </div>

                <div style={styles.scrollableDiv}>
                    <table cellPadding={0} cellSpacing={0} style={{ width: 'max-content', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                        <colgroup>
                            <col style={{ width: '139px' }} />
                            <col style={{ width: '100px' }} />
                            <col style={{ width: '80px' }} />
                            <col style={{ width: '60px' }} />
                            <col style={{ width: '100px' }} />
                            <col style={{ width: '100px' }} />
                            <col style={{ width: '100px' }} />
                            <col style={{ width: '100px' }} />
                        </colgroup>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                            <tr style={styles.headerRow}>
                                <th style={styles.headerCell}>Title</th>
                                <th style={styles.headerCell}>Date Added</th>
                                <th style={styles.headerCell}>Type</th>
                                <th style={{ ...styles.headerCell, textAlign: 'right' }}>Price</th>
                                <th style={styles.headerCell}>Start Date</th>
                                <th style={styles.headerCell}>Allocated to</th>
                                <th style={styles.headerCell}>Purchased</th>
                                <th style={styles.headerCell}>ActiveUsage</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <Loading />
                            ) : (
                                currentData.map((row, index) => {
                                    const isSelected = selectedAvatar && selectedAvatar.Title === row.Title;
                                    let rowStyle = index % 2 === 0 ? styles.altRow : styles.row;
                                    if (isSelected) rowStyle = styles.selectedRow;

                                    return (
                                        <tr
                                            key={row.Title}
                                            style={rowStyle}
                                            onClick={() => handleRowClick(row)}
                                        >
                                            <td style={styles.cell}>{row.Title}</td>
                                            <td style={styles.cell}>{formatDate(row.DateAdded)}</td>
                                            <td style={styles.cell}>{row.Type}</td>
                                            <td style={{ ...styles.cell, textAlign: 'right' }}>{row.Price}</td>
                                            <td style={styles.cell}>{formatDate(row.StartDate)}</td>
                                            <td style={styles.cell}>{row.AllocatedTo}</td>
                                            <td style={styles.cell}>
                                                {row.Purchased > 0 ? row.Purchased : ''}
                                            </td>

                                            <td style={{...styles.cell, width:"1500px"}}>
                                                {row.ActiveUsage > 0 ? row.ActiveUsage : ''}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                <div style={styles.paginationBar}>
                    <span style={styles.navLink} onClick={() => handlePageChange('first')}>&lt;&lt;</span>
                    <span style={styles.navLink} onClick={() => handlePageChange('prev')}>&lt;</span>
                    <span style={{ margin: '0 10px' }}>
                        {currentPage} of {totalPages || 1}
                    </span>
                    <span style={styles.navLink} onClick={() => handlePageChange('next')}>&gt;</span>
                    <span style={styles.navLink} onClick={() => handlePageChange('last')}>&gt;&gt;</span>
                </div>

                <div style={styles.buttonContainer}>
                    <button
                        style={styles.button}
                        onClick={() => fn_ShowARDAvatar('A')}
                    >
                        Add
                    </button>

                    <button
                        style={{...selectedAvatar ? styles.button : styles.buttonDisabled, border:"1px solid #767676"}}
                        disabled={!selectedAvatar}
                        onClick={() => fn_ShowARDAvatar('R')}
                    >
                        View / Modify
                    </button>

                    <button
                        style={styles.button}
                        onClick={fn_ViewPurchases}
                    >
                        View Purchases
                    </button>

                    <button
                        style={styles.button}
                        onClick={fn_FreeAvatars}
                    >
                        Free Avatars
                    </button>

                    <button
                        style={styles.button}
                        onClick={fn_Back}
                    >
                        Back
                    </button>
                </div>
                {showARD && (
                    <EL_AvatarARD
                        mode={ardMode}
                        avatarTitle={selectedAvatar?.Title}
                        onClose={() => setShowARD(false)}
                        onSaveSuccess={fetchAvatars}
                        showWarning={handleShowWarning}
                        showConfirm={handleShowConfirm}
                    />
                )}

                {showPurchases && selectedAvatar && (
                    <EL_ViewAvatarPurchases
                        avatarTitle={selectedAvatar.Title}
                        totalPurchases={selectedAvatar.Purchased}
                        onClose={() => setShowPurchases(false)}
                        showWarning={handleShowWarning}
                        showConfirm={handleShowConfirm}
                    />
                )}
                {alertMsg && (
                    <CustomAlert
                        message={alertMsg}
                        onClose={() => setAlertMsg('')}
                    />
                )}
                {confirmConfig && <ConfirmDialog />}
            </div>
        </div>
    );
}